import { useCreateApp, useUserActiveOrganization } from "@/api";
import TooltipHover from "@/components/ui/TooltipHover";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useTaskContext } from "@/contexts/useTaskContext";
import { AppDeploymentStatus } from "@/gql/__generated__/graphql";
import { DialogClose } from "@radix-ui/react-dialog";
import { Info, Loader2 } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { projectTemplates } from "../../assets/projectTemplates";
import { extractOwnerAndRepo } from "../../hooks/getOwnerAndRepoFromGitProject";
import { ProjectTemplate } from "../../types/app.type";
import CreateAppDetails from "./CreateAppDetails";
import CreateAppProjects from "./CreateAppProjects";
import CreateAppViaDocumentation from "./CreateAppViaDocumentation";
import { CustomSubmit } from "./LocalProject/types/customSubmit.type";
import { MissingInputsError } from "./LocalProject/utils/error";

type Props = {
  children: ReactNode;
};

const QuickCreateAppModal = ({ children }: Props) => {
  const [customSubmit, setCustomSubmit] = useState<CustomSubmit | null>(null);
  const [loadingCustomSubmit, setLoadingCustomSubmit] = useState(false);
  const [isCreateAppDialogOpen, setIsCreateAppDialogOpen] = useState(false);
  const [gitLinkError, setGitLinkError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [selectedProjectForApp, setSelectedProjectForApp] =
    useState<ProjectTemplate | null>(projectTemplates[0]);
  const { activeOrganization } = useUserActiveOrganization();
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const useCLIId = "use-cli";
  const publicGithubLinkId = "use-public-repo";
  const localProjectId = "local-project";

  const [quickCreateApp, { loading: loadingCreatingApp }] = useCreateApp();
  const { startBgTasks, taskStatus, newAppID, startTaskListener } =
    useTaskContext();

  const isDeployingNewApp =
    !!newAppID && taskStatus === AppDeploymentStatus.Pending;

  useEffect(() => {
    setGitLinkError(null);
    setNameError(null);
    setUrlError(null);
  }, [selectedProjectForApp]);

  const handleProjectSelection = (projectName: string) => {
    const selected =
      projectTemplates.find((template) => template.name === projectName) ||
      null;
    setSelectedProjectForApp(selected);
  };

  const appDeployedToast = () => {
    toast({
      title: "Deploying your app",
      description: "The app is being deployed - This may take a moment."
    });
  };

  const handleCustomSubmit = async (form: HTMLFormElement) => {
    if (!customSubmit) return;
    setLoadingCustomSubmit(true);
    const customData = await customSubmit(form);
    startTaskListener(customData.appId, customData.deployVersionId);

    form.reset();
    setLoadingCustomSubmit(false);
    setIsCreateAppDialogOpen(false);

    appDeployedToast();
  };

  async function handleSubmit() {
    const form = formRef.current;
    if (
      !form ||
      !activeOrganization ||
      !selectedProjectForApp ||
      selectedProjectForApp?.id === useCLIId
    )
      return toast({
        title: "Something went wrong",
        description: "Please refresh and try again or report the issue."
      });
    try {
      const formData = new FormData(form);

      if (customSubmit) {
        await handleCustomSubmit(form);
        return null;
      }

      const appSlug = formData.get("appSlug") as string;
      const appSlugTrimmed = appSlug.trim();
      const displayName = formData.get("appName") as string;
      const displayNameTrimmed = displayName.trim();
      const description = formData.get("appDescription") as string;
      const descriptionTrimmed = description.trim();
      const gitLink = formData.get("githubLink") as string;
      const gitLinkTrimmed = gitLink && gitLink.trim();

      let hasError = false;
      let extractionResults: ReturnType<typeof extractOwnerAndRepo> = null;

      if (selectedProjectForApp.id === publicGithubLinkId && !gitLinkTrimmed) {
        setGitLinkError("URL cannot be empty");
        hasError = true;
      } else {
        setGitLinkError(null);
        extractionResults = extractOwnerAndRepo(
          selectedProjectForApp.id === publicGithubLinkId
            ? gitLinkTrimmed
            : selectedProjectForApp?.githubLink
        );
        if (!extractionResults) {
          setGitLinkError(
            "Repository not found, please try again with a valid GitHub URL"
          );
          hasError = true;
        }
      }
      if (!displayNameTrimmed) {
        setNameError("Name cannot be empty");
        hasError = true;
      } else {
        setNameError(null);
      }
      if (!appSlugTrimmed) {
        setUrlError("Slug cannot be empty");
        hasError = true;
      } else {
        setUrlError(null);
      }

      if (hasError || !extractionResults) return null;

      const appInfo = {
        appSlug: appSlugTrimmed,
        displayName: displayNameTrimmed,
        description: descriptionTrimmed
      };

      //**** Create app
      const { data: createAppData } = await quickCreateApp({
        variables: {
          organizationSlug: activeOrganization?.slug,
          appData: appInfo
        }
      });
      if (!createAppData) return null;

      startBgTasks(
        createAppData.appCreate.id,
        extractionResults?.owner,
        extractionResults?.repo
      );

      form.reset();
      setIsCreateAppDialogOpen(false);
      appDeployedToast();
    } catch (submitError) {
      setLoadingCustomSubmit(false);
      if (submitError === MissingInputsError) return null;
      form.reset();
      toast({
        title: "App creation failed",
        description: `Failed to create app, please try again or report the issue.
        ${submitError}`
      });
      console.error("An error occurred:", submitError);
    }
    return undefined;
  }

  return (
    <Dialog.Root
      open={isCreateAppDialogOpen}
      onOpenChange={setIsCreateAppDialogOpen}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Content className="max-w-5xl dark:bg-brand-gray-900 max-h-screen flex flex-col flex-grow">
        <Dialog.Header className="space-y-2 relative flex-shrink-0  flex-grow-0 flex-auto">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Deploy an App:
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300 mb-4 mt-4">
            Select one of our project templates or deploy your own project! The
            app will be visible to everyone in this organization.
            <span className="block text-brand-gray-500 dark:text-brand-gray-500">
              <i>
                Note: You will be able to make changes to the app after
                deployment by using the edit feature on the app card.
              </i>
            </span>
          </Dialog.Description>
        </Dialog.Header>
        <form
          ref={formRef}
          className="grid gap-4 mt-8 mr-2 overflow-auto flex-grow"
        >
          <CreateAppProjects
            selectedProjectForAppName={
              selectedProjectForApp && selectedProjectForApp?.name
            }
            handleProjectSelection={handleProjectSelection}
          />
          {selectedProjectForApp && selectedProjectForApp?.id !== useCLIId ? (
            <CreateAppDetails
              selectedTemplate={selectedProjectForApp}
              gitLinkError={gitLinkError}
              nameError={nameError}
              urlError={urlError}
              setSubmit={setCustomSubmit}
            />
          ) : (
            <CreateAppViaDocumentation />
          )}
        </form>

        <Dialog.Footer className="mt-8 flex-shrink-0  flex-grow-0 flex-auto">
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setNameError(null);
                setUrlError(null);
                setGitLinkError(null);
                setSelectedProjectForApp(projectTemplates[0]);
                setCustomSubmit(null);
              }}
              disabled={loadingCreatingApp || loadingCustomSubmit}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            type="submit"
            className="disabled:cursor-not-allowed"
            onClick={handleSubmit}
            disabled={
              selectedProjectForApp?.id === useCLIId ||
              loadingCreatingApp ||
              isDeployingNewApp ||
              (selectedProjectForApp?.id === localProjectId &&
                customSubmit === null) ||
              loadingCustomSubmit
            }
          >
            {selectedProjectForApp?.id === localProjectId
              ? "Save and Deploy"
              : "Deploy"}
            {loadingCreatingApp ||
              (loadingCustomSubmit && (
                <Loader2 className="mx-2 h-4 w-4 animate-spin" />
              ))}
          </Button>
          {isDeployingNewApp && (
            <TooltipHover text="You can only deploy one app at a time">
              <Info size={18} />
            </TooltipHover>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default QuickCreateAppModal;
